import { FC } from "react";
import { Box, Button as MuiButton, Typography, useTheme } from "@mui/material";
import {
  Geozone,
  Geozones,
  GoogleMap,
  Marker,
  MarkerInfoWindow,
  Markers,
  MarkersClusterer,
  MarkersClustererInfoWindow,
  MarkerTooltip,
  types,
  utils
} from "@vilocnv/allsetra-core";
import useDashboardMapPage from "./useDashboardMapPage";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardMapActionsButtons from "./DashboardMapActionsButtons";
import DashboardMapFilter from "./DashboardMapFilter";
import LastTripsPanel from "../../components/LastTripsPanel";
import LastTripsRouteOverlay from "../../components/LastTripsRouteOverlay";
import { useAppSelector } from "hooks";
import { selectAccountFeaturesState } from "app/data/selectors";
import ObjectsMapListing from "pages/dashboard/Objects/ObjectsMapLIsting";
import { CloseGreenIcon, OpenGreenIcon } from "assets/icons";
import {
  CollapseTogglePill,
  ExpandPanelFloatingContainer,
  MapActionsFloatingContainer,
  MapCanvasWrapper,
  MapPageWrapper,
  SidePanelHeader,
  SidePanelResizeHandle,
  SidePanelScrollableBox,
  SidePanelToolbarRow,
  SidePanelWrapper
} from "./Map.styled";

const Map: FC = () => {
  const { t, i18n } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { features } = useAppSelector(selectAccountFeaturesState);
  const {
    mapRef,
    accountId,
    objectFilters,
    markers,
    showClusters,
    showSatelliteMode,
    showTraffic,
    geozones,
    showGeozones,
    showInactiveObjects,
    isLoadingObjects,
    isLoadingObjectDetail,
    groups,
    isLoadingGroups,
    keys,
    isLoadingKeys,
    objectTypes,
    isLoadingObjectTypes,
    selectedTooltipMarker,
    selectedObject,
    selectedCluster,
    modalFilterOpen,
    sideMenuObjects,
    sideMenuObjectsLoading,
    totalSideMenuRecords,
    sideMenuResolvedAddresses,
    sideMenuResolvedAddressLoading,
    isSidePanelCollapsed,
    sidePanelWidth,
    isResizingSidePanel,
    handleMapLoad,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    handleClusterClick,
    handleClusterCloseInfoWindowClick,
    handleShowSatelliteButtonClick,
    handleShowClustersButtonClick,
    handleShowTrafficButtonClick,
    handleShowGeozonesButtonClick,
    handleFilterModalCloseClick,
    handleFilterModalOpenClick,
    handleModalFilterFormSubmit,
    handleFilterChange,
    isSelectOpen,
    setIsSelectOpen,
    addressInfo,
    lastTripsPanelOpen,
    lastTrips,
    lastTripsLoading,
    lastTripsError,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    handleOpenLastTripsPanel,
    handleCloseLastTripsPanel,
    lastTripsObjectName,
    handleSelectLastTrip,
    autoFitVisibleUnits,
    handleAutoFitVisibleUnitsButtonClick,
    handleToggleSidePanelCollapse,
    handleSidePanelResizeMouseDown,
    handleSidePanelResizeKeyDown
  } = useDashboardMapPage();

  const showLastTripsAction =
    features.isRidesEnabled && Boolean(selectedObject?.uniqueId);

  return (
    <MapPageWrapper isResizing={isResizingSidePanel}>
      <SidePanelWrapper
        panelWidth={sidePanelWidth}
        isCollapsed={isSidePanelCollapsed}
        data-testid="map-objects-side-panel"
      >
        <SidePanelHeader>
          <DashboardMapFilter
            markers={markers}
            isFilterModalOpen={modalFilterOpen}
            onFilterModalCloseClick={handleFilterModalCloseClick}
            onFilterModalOpenClick={handleFilterModalOpenClick}
            onModalFormSubmit={handleModalFilterFormSubmit}
            group={groups}
            isLoadingGroups={isLoadingGroups}
            objectTypes={objectTypes}
            isLoadingObjectTypes={isLoadingObjectTypes}
            activeAccountKeys={keys}
            isLoadingActiveAccountKeys={isLoadingKeys}
            mapFilters={objectFilters}
            showInactiveObjects={showInactiveObjects}
            onFilterChange={handleFilterChange}
            theme={theme}
            t={t}
            setIsSelectOpen={setIsSelectOpen}
            isSelectOpen={isSelectOpen}
            showSearch={true}
            isLoadingSearch={Boolean(sideMenuObjectsLoading)}
          />
          <SidePanelToolbarRow>
            <CollapseTogglePill
              onClick={handleToggleSidePanelCollapse}
              data-testid="collapse-objects-panel-button"
            >
              <CloseGreenIcon />
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "26px"
                }}
              >
                Collapse objects
              </Typography>
            </CollapseTogglePill>
          </SidePanelToolbarRow>
        </SidePanelHeader>
        <SidePanelScrollableBox id="scrollableDiv">
          <ObjectsMapListing
            drawerSelectedAccountId={accountId}
            sideMenuObjects={sideMenuObjects}
            sideMenuObjectsLoading={sideMenuObjectsLoading}
            totalSideMenuRecords={totalSideMenuRecords}
            resolvedAddresses={sideMenuResolvedAddresses}
            resolvedAddressLoading={sideMenuResolvedAddressLoading}
            showInactiveObjects={showInactiveObjects}
            onObjectSelect={handleShowInfoWindowClick}
          />
        </SidePanelScrollableBox>
      </SidePanelWrapper>

      {!isSidePanelCollapsed && (
        <SidePanelResizeHandle
          isResizing={isResizingSidePanel}
          onMouseDown={handleSidePanelResizeMouseDown}
          onKeyDown={handleSidePanelResizeKeyDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize objects side panel"
          aria-valuenow={sidePanelWidth}
          tabIndex={0}
          data-testid="side-panel-resize-handle"
        />
      )}

      <MapCanvasWrapper isSidePanelCollapsed={isSidePanelCollapsed}>
        {isSidePanelCollapsed && (
          <ExpandPanelFloatingContainer>
            <CollapseTogglePill
              onClick={handleToggleSidePanelCollapse}
              data-testid="expand-objects-panel-button"
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "26px"
                }}
              >
                Expand objects
              </Typography>
              <OpenGreenIcon />
            </CollapseTogglePill>
          </ExpandPanelFloatingContainer>
        )}
        <MapActionsFloatingContainer data-testid="map-display-preferences-buttons">
          <DashboardMapActionsButtons
            showClusters={showClusters}
            onShowClustersButtonClick={handleShowClustersButtonClick}
            showSatelliteMode={showSatelliteMode}
            onShowSatelliteButtonClick={handleShowSatelliteButtonClick}
            showTraffic={showTraffic}
            onShowTrafficButtonClick={handleShowTrafficButtonClick}
            showGeozones={showGeozones}
            onShowGeozonesButtonClick={handleShowGeozonesButtonClick}
            autoFitVisibleUnits={autoFitVisibleUnits}
            onAutoFitVisibleUnitsButtonClick={
              handleAutoFitVisibleUnitsButtonClick
            }
          />
        </MapActionsFloatingContainer>
        <GoogleMap
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY!}
          onLoad={handleMapLoad}
          showSatelliteMode={showSatelliteMode}
          showTraffic={showTraffic}
          isLoading={isLoadingObjects}
          zoom={7}
          options={{
            disableDefaultUI: false,
            fullscreenControl: false,
            mapTypeControl: false,
            zoomControl: false
          }}
        >
          <Markers markers={markers} showClusters={showClusters}>
            {(marker: types.MarkerType) => {
              return (
                <Marker
                  uniqueId={marker.uniqueId}
                  position={marker.position}
                  icon={utils.toGoogleMapsIcon(marker.icon)}
                  onMouseOver={() => handleMarkerMouseOver(marker.uniqueId)}
                  onMouseOut={() => handleMarkerMouseOut()}
                  onClick={() => handleShowInfoWindowClick(marker.uniqueId)}
                />
              );
            }}
          </Markers>
          {selectedTooltipMarker && (
            <MarkerTooltip
              position={selectedTooltipMarker.position}
              label={selectedTooltipMarker.name}
            />
          )}
          {selectedObject &&
            selectedObject?.location?.latitude &&
            selectedObject?.location?.longitude && (
              <MarkerInfoWindow
                objectData={selectedObject}
                position={{
                  lat: selectedObject.location.latitude,
                  lng: selectedObject.location.longitude
                }}
                onCloseClick={() => handleCloseInfoWindowClick()}
                isLoadingLocation={isLoadingObjectDetail}
                isLoadingMetaData={isLoadingObjectDetail}
                translatorFn={t}
                navigateFn={navigate}
                addressInfo={addressInfo}
                actions={
                  showLastTripsAction ? (
                    <MuiButton
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenLastTripsPanel(
                          selectedObject?.uniqueId,
                          selectedObject?.name
                        );
                      }}
                      sx={{
                        color: "#FFFFFF",
                        borderColor: "rgba(255, 255, 255, 0.6)",
                        "&:hover": {
                          borderColor: "#FFFFFF",
                          backgroundColor: "rgba(255, 255, 255, 0.08)"
                        }
                      }}
                    >
                      {t("common.lastTrips")}
                    </MuiButton>
                  ) : undefined
                }
              />
            )}
          <LastTripsRouteOverlay
            mapRef={mapRef}
            selectedTrip={selectedTrip}
            rideLocations={rideLocations}
          />
          <MarkersClusterer
            markers={markers}
            showClusters={showClusters}
            onClick={handleClusterClick}
          >
            {(marker, clusterer) => (
              <Marker
                key={marker.uniqueId}
                uniqueId={marker.uniqueId}
                position={marker.position}
                icon={marker.icon}
                onMouseOver={() => handleMarkerMouseOver(marker.uniqueId)}
                onMouseOut={() => handleMarkerMouseOut()}
                onClick={() => handleShowInfoWindowClick(marker.uniqueId)}
                clusterer={clusterer}
                noClustererRedraw={true}
              />
            )}
          </MarkersClusterer>
          {selectedCluster && (
            <MarkersClustererInfoWindow
              cluster={selectedCluster}
              markers={markers}
              onClose={handleClusterCloseInfoWindowClick}
              translatorFn={t}
              navigateFn={navigate}
            />
          )}
          <Geozones geozones={geozones} showGeozones={showGeozones}>
            {(geozone) => <Geozone geozone={geozone} />}
          </Geozones>
        </GoogleMap>

        <LastTripsPanel
          open={lastTripsPanelOpen}
          objectName={
            lastTripsObjectName || selectedObject?.name || t("typography.na")
          }
          trips={lastTrips}
          loading={lastTripsLoading}
          error={lastTripsError}
          selectedTripId={selectedTripId}
          locationsLoading={locationsLoading}
          locationsError={locationsError}
          onClose={handleCloseLastTripsPanel}
          onSelectTrip={handleSelectLastTrip}
          translator={t}
          currentLocale={i18n.language}
        />
      </MapCanvasWrapper>
    </MapPageWrapper>
  );
};

export default Map;
